import { Modal } from '@/components/ui/modal';
import { IconComponent } from '../../../util/icons';
import exclamation from '../../../asset/icons/exclamation-red.svg';
import { useNavigationService } from '../../../services/navigate-service';
import { ROUTES, buildRoute } from '../../../config/routes';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { refusedSyncActions, type FailedSyncActions } from '../pmsNoticeActions';
import type { SyncFailure } from './syncFailure';

interface FailedAssetSyncProps {
  /** Callers mount the notice only while a failure is held. */
  failure: SyncFailure;
  onClose: () => void;
  propertyAddress: string;
  pmsSource: string;
  /** DV-3345: which buttons show, by entry and role (pmsNoticeActions). */
  actions: FailedSyncActions;
}

const FailedAssetSync = ({
  failure,
  onClose,
  propertyAddress,
  pmsSource,
  actions: actionsForUploadFailure
}: FailedAssetSyncProps) => {
  const { navigateTo } = useNavigationService();
  const actions =
    failure.kind === 'refused'
      ? refusedSyncActions(actionsForUploadFailure)
      : actionsForUploadFailure;

  const redirectToVendorConnection = () => {
    navigateTo(buildRoute.integrationVendor(pmsSource));
  };

  const redirectToContactUS = () => {
    navigateTo(ROUTES.HELP_CONTACT, { openSupport: 'true' });
  };

  return (
    <Modal
      open
      onOpenChange={onClose}
      size="lg"
      title={
        <div className="flex items-center gap-2">
          <IconComponent
            src={exclamation}
            width={32}
            height={32}
            alt="error"
          />
          <Typography variant="heading-lg" className="font-medium">
            Sync Asset Failed
          </Typography>
        </div>
      }
    >
      <div className="flex flex-col gap-3 pt-4 pb-4 px-6">
        <Typography className="text-lg">
          {failure.kind === 'refused'
            ? failure.message
            : `There seems to be some issues with syncing the assets for the below property. Please check the ${pmsSource} connection or contact the system administrator.`}
        </Typography>
        <Typography variant="body">Address</Typography>
        <div className="flex flex-col gap-3 rounded-lg bg-info-soft p-4">
          <div>
            <Typography className="text-lg">{propertyAddress}</Typography>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 border-t border-border px-6 pt-4">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          {actions.showClose && (
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={onClose}
            >
              Close
            </Button>
          )}
          {actions.contactSupport && (
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => redirectToContactUS()}
            >
              Contact Support
            </Button>
          )}
          {actions.checkConnection?.kind === 'navigate' && (
            <Button
              type="button"
              className="w-full sm:w-auto"
              onClick={() => redirectToVendorConnection()}
            >
              Check Connection
            </Button>
          )}
          {actions.checkConnection?.kind === 'link' && (
            <Button asChild className="w-full sm:w-auto">
              <a
                href={actions.checkConnection.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                Check Connection
              </a>
            </Button>
          )}
        </div>
        {actions.newTabCaption && (
          <Typography
            variant="body-sm"
            className="text-muted-foreground sm:text-right"
          >
            {actions.newTabCaption}
          </Typography>
        )}
      </div>
    </Modal>
  );
};

export default FailedAssetSync;
